import { sectionLabels } from "../section-labels";
import { surfaceField } from "./_shared";
/**
 * Bottom navigation — the mobile/tablet app-style tab bar pinned to the bottom
 * edge (`BuyerBottomNav`, `lg:hidden`). Split out of the header (2026-07) so it
 * is its own section: the seller edits it independently, it carries its own
 * editor anchor, and its controls are naturally mobile-only.
 *
 * Locked (can't be reordered/removed) and home-only: like the old
 * `header.bottomNav`, it is GLOBAL chrome sourced from the home template and
 * rendered on every storefront page. `variant: 'bnav-off'` (default) renders
 * nothing, so it doubles as the "hide" control — hence NOT `criticalForSeo`.
 *
 * The tab set (logo · home · cart · search · orders) is still hard-coded in
 * `BuyerBottomNav`; making the items seller-configurable is a follow-up that
 * will add a `nav-item` block type to this section.
 */
export const navSchema = {
    type: "nav",
    name: sectionLabels["nav"],
    description: {
        ar: "شريط التنقل السفلي على الموبايل والتابلت — بيدي تجربة قريبة من التطبيقات.",
        en: "The mobile/tablet bottom navigation bar — a native-app-style tab bar.",
    },
    locked: true,
    // Home-only: global chrome edited once from the home template and rendered on
    // every page (mirrors how the old `header.bottomNav` was sourced from home).
    availableOnTemplates: ["home"],
    maxPerTemplate: 1,
    settings: [
        // Bottom-bar background — the standard section `surface` slot, so the seller
        // colours the bar from "How it looks" with the same options as any section.
        // Default `page` keeps each variant's current look until a colour is picked.
        surfaceField("page"),
        {
            // Kept the `bottomNav` id (moved verbatim from the header): it's in
            // `ALWAYS_BASE_FIELD_IDS`, so edits write to base settings even on the
            // mobile tab. That matters because this is a STRUCTURAL choice (which bar
            // component renders) — SSR can't render a different bar per breakpoint, so
            // it must never be responsive-split. Options/labels/default unchanged.
            id: "bottomNav",
            type: "select",
            ui: "thumbnail",
            // The whole bar is `lg:hidden` — phone/tablet only.
            appliesTo: ["mobile", "tablet"],
            label: { ar: "شكل الشريط السفلي", en: "Bottom bar style" },
            helpText: {
                ar: "شريط تنقل سفلي يظهر على الموبايل والتابلت ليعطي تجربة قريبة من التطبيقات.",
                en: "A bottom navigation bar shown on phone/tablet for a native-app feel.",
            },
            options: [
                { value: "bnav-off", label: { ar: "بدون", en: "Off" } },
                { value: "bnav-tabbar", label: { ar: "كلاسيكي", en: "Classic tabs" } },
                { value: "bnav-floating", label: { ar: "عائم", en: "Floating" } },
                { value: "bnav-fab", label: { ar: "زر مركزي", en: "Center action" } },
                { value: "bnav-pill", label: { ar: "حبة نشطة", en: "Active pill" } },
                { value: "bnav-dock", label: { ar: "لوحة", en: "Dock" } },
            ],
            // Default-ON: every theme wears the app-native tab bar on phone/tablet.
            // A seller who wants the classic web footer nav sets `bnav-off` explicitly.
            default: "bnav-tabbar",
        },
        // Cart-drawer chrome — how each item row behaves. Lives here (not the
        // header) because the cart is reached from the bottom bar. `rows-trash`
        // keeps the classic delete (X) button; `rows-swipe` adds native
        // swipe-to-delete on phones. Also `ALWAYS_BASE_FIELD_IDS` (a global cart
        // behavior, never responsive-split).
        {
            id: "cartRowStyle",
            type: "select",
            // `rows-swipe` only changes behavior on phones: `SwipeableCartItem` + the
            // X-button hide are gated at Tailwind `sm` (<640px) in
            // `cart/CartContents.tsx`/`SwipeableCartItem.tsx`. Mobile-only.
            appliesTo: ["mobile"],
            label: { ar: "حذف المنتج من السلة", en: "Cart item removal" },
            helpText: {
                ar: "اختر طريقة حذف المنتج من سلة التسوق: زر حذف، أو سحب للحذف على الموبايل زي التطبيقات.",
                en: "How shoppers remove an item from the cart: a delete button, or native swipe-to-delete on mobile.",
            },
            options: [
                { value: "rows-trash", label: { ar: "زر حذف", en: "Delete button" } },
                {
                    value: "rows-swipe",
                    label: { ar: "سحب للحذف", en: "Swipe to delete" },
                },
            ],
            default: "rows-trash",
        },
    ],
    blocks: [
        // One bottom-bar tab per block, ONE per `kind`. The editor shows a fixed
        // checklist of the six kinds (`NavItemChecklist`): checking a kind inserts
        // its block, unchecking removes it; the bar renders the checked kinds in the
        // canonical order below. An EXPLICIT empty list (all unchecked) → an empty
        // bar; only a store with NO `blocks` array at all falls back to the default
        // set. Each `kind` maps to a fixed icon + destination in `BuyerBottomNav`;
        // `menu` opens the shared nav drawer.
        {
            type: "nav-item",
            name: { ar: "عنصر في الشريط", en: "Bottom bar item" },
            description: {
                ar: "أيقونة واحدة في شريط التنقل السفلي.",
                en: "A single icon in the bottom navigation bar.",
            },
            // No cap: the checklist is a fixed set of six kinds (one block each).
            settings: [
                {
                    id: "kind",
                    type: "select",
                    // Set by the checklist row, not edited in the per-item view.
                    label: { ar: "النوع", en: "Item" },
                    options: [
                        {
                            value: "store",
                            label: { ar: "المتجر (اللوجو)", en: "Store (logo)" },
                        },
                        { value: "home", label: { ar: "الرئيسية", en: "Home" } },
                        { value: "search", label: { ar: "بحث", en: "Search" } },
                        { value: "saved", label: { ar: "المفضلة", en: "Saved" } },
                        { value: "cart", label: { ar: "السلة", en: "Cart" } },
                        {
                            value: "menu",
                            label: { ar: "القائمة (٣ نقاط)", en: "Menu (3 dots)" },
                        },
                    ],
                    default: "home",
                },
                {
                    id: "label",
                    type: "text",
                    i18n: true,
                    label: { ar: "النص", en: "Label" },
                    helpText: {
                        // Seeded with the kind's system title on add; clearing it → the
                        // item shows just its icon (no label).
                        ar: "امسح النص عشان تعرض الأيقونة لوحدها من غير كلام.",
                        en: "Clear the text to show just the icon with no label.",
                    },
                    validate: { max: 20 },
                },
            ],
        },
    ],
};
//# sourceMappingURL=nav.js.map